import { useState } from 'react';
import { getItem } from '../../../../utils/localStorage';
import { makeRequest2 } from '../../../../utils/makeRequest';
import {
    approveCashPayment,
    rejectCashPayment,
    voidCashPayment,
    applyOverpayment,
    transferCredit
} from '../../../../utils/urls';
import { toastify } from '../../../../utils/toast';

export const useFacilityPayments = (refreshPayments, fetchUnpaidInvoicesAPI, fetchInvoiceDetailsAPI) => {
    // Dialog States
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [approveDialogVisible, setApproveDialogVisible] = useState(false);
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
    const [voidDialogVisible, setVoidDialogVisible] = useState(false);
    const [overpaymentDialogVisible, setOverpaymentDialogVisible] = useState(false);
    const [transferCreditDialogVisible, setTransferCreditDialogVisible] = useState(false);
    const [comments, setComments] = useState('');
    const [loading, setLoading] = useState(false);

    // Overpayment related states
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [overpaymentAmount, setOverpaymentAmount] = useState(0);
    const [remainingOverpayment, setRemainingOverpayment] = useState(0);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    // Utility function to map between UI labels and schema values
    const mapPaymentMethod = (uiMethod) => {
        // Check if we need to do a mapping for the Create Payment Modal
        // In the Create Payment Modal, values are passed as "Cash", "Check", "Bank Transfer"
        if (uiMethod === 'Cash') return 'cash';
        if (uiMethod === 'Check') return 'cheque';
        if (uiMethod === 'Bank Transfer') return 'bank-transfer';

        // If it's already in the correct format (from our Approve Dialog), return as is
        return uiMethod;
    };

    // Utility function to format payment method for display
    const formatPaymentMethod = (methodValue) => {
        if (!methodValue) return 'Cash';

        switch (methodValue.toLowerCase()) {
            case 'cash':
                return 'Cash';
            case 'cheque':
                return 'Cheque';
            case 'bank-transfer':
                return 'Bank Transfer';
            default:
                return methodValue.charAt(0).toUpperCase() + methodValue.slice(1).replace('-', ' ');
        }
    };

    const formatDate = (date) => {
        if (!date) return '';
        const dateObj = new Date(date);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        return `${day} ${month} ${hours}:${minutes}:${seconds}`;
    };

    const formatCurrency = (amount, currencyCode = 'KES') => {
        return `${currencyCode} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const handleApprovePayment = async () => {
        try {
            setLoading(true);
            if (!selectedPayment?._id) {
                throw new Error('No payment selected for approval');
            }

            // Get the payment method from the selected payment record
            const paymentMethodFromRecord = selectedPayment.paymentMethod || 'Cash';

            // Map from UI-friendly value to schema value
            const schemaPaymentMethod = mapPaymentMethod(paymentMethodFromRecord);

            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(
                `${approveCashPayment}/${facilityId}/${selectedPayment._id}`,
                'POST',
                {
                    comments,
                    paymentMethod: schemaPaymentMethod
                }
            );

            if (response.success) {
                // Check if this payment resulted in an overpayment
                if (response.data?.reconciliationStatus === 'Overpaid') {
                    // Set the selected payment with the updated data
                    setSelectedPayment(response.data);

                    // Calculate overpayment amount from reconciliation details
                    const overpayAmount = response.data.reconciliationDetails?.overpayAmount || 0;
                    setOverpaymentAmount(overpayAmount);
                    setRemainingOverpayment(overpayAmount);

                    // Fetch unpaid invoices for this customer
                    await fetchUnpaidInvoices(response.data.client.clientId);

                    // Show the overpayment dialog
                    setOverpaymentDialogVisible(true);
                } else {
                    await refreshPayments();
                    toastify('Payment approved successfully', 'success');
                    setApproveDialogVisible(false);
                    setSelectedPayment(null);
                    setComments('');
                }
            } else {
                throw new Error(response.error || response.message || 'Failed to approve payment');
            }
        } catch (error) {
            console.error('Approve payment error:', error);
            toastify(error.message, 'error');
            setApproveDialogVisible(false);
            setSelectedPayment(null);
            setComments('');
        } finally {
            setLoading(false);
        }
    };

    const handleRejectPayment = async () => {
        try {
            setLoading(true);
            if (!selectedPayment?._id) {
                throw new Error('No payment selected for rejection');
            }

            if (!comments.trim()) {
                throw new Error('Please provide a reason for rejection');
            }

            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(
                `${rejectCashPayment}/${facilityId}/${selectedPayment._id}`,
                'POST',
                { comments }
            );

            if (response.success) {
                await refreshPayments();
                toastify('Payment rejected successfully', 'success');
            } else {
                throw new Error(response.message || 'Failed to reject payment');
            }
        } catch (error) {
            console.error('Reject payment error:', error);
            toastify(error.message, 'error');
        } finally {
            setRejectDialogVisible(false);
            setSelectedPayment(null);
            setComments('');
            setLoading(false);
        }
    };

    const handleVoidPayment = async () => {
        try {
            setLoading(true);
            if (!selectedPayment?._id) {
                throw new Error('No payment selected for voiding');
            }

            if (!comments.trim()) {
                throw new Error('Please provide a reason for voiding');
            }

            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(
                `${voidCashPayment}/${facilityId}/${selectedPayment._id}`,
                'POST',
                { voidReason: comments }
            );

            if (response.success) {
                await refreshPayments();
                toastify('Payment voided successfully', 'success');
            } else {
                throw new Error(response.message || 'Failed to void payment');
            }
        } catch (error) {
            console.error('Void payment error:', error);
            toastify(error.message, 'error');
        } finally {
            setVoidDialogVisible(false);
            setSelectedPayment(null);
            setComments('');
            setLoading(false);
        }
    };

    const handleTransferCredit = async (transferData) => {
        try {
            setLoading(true);
            
            if (!transferData.sourceAccountNumber || !transferData.destinationAccountNumber) {
                throw new Error('Source and destination account numbers are required');
            }

            if (!transferData.amount || transferData.amount <= 0) {
                throw new Error('Transfer amount must be greater than zero');
            }

            const facilityId = await getItem('selectedFacilityId');
            const userId = await getItem('userId');
            
            // Add userId to the transfer data
            const requestData = {
                ...transferData,
                userId
            };
            
            const response = await makeRequest2(
                `${transferCredit}/${facilityId}`,
                'POST',
                requestData
            );

            if (response.success) {
                await refreshPayments();
                toastify('Credit transferred successfully', 'success');
                setTransferCreditDialogVisible(false);
                setSelectedPayment(null);
            } else {
                throw new Error(response.message || 'Failed to transfer credit');
            }
        } catch (error) {
            console.error('Transfer credit error:', error);
            toastify(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchUnpaidInvoices = async (clientId) => {
        try {
            setLoadingInvoices(true);

            if (!clientId) {
                toastify('Client ID is required', 'error');
                setUnpaidInvoices([]);
                return;
            }

            // Use the provided API function to fetch unpaid invoices
            const invoicesData = await fetchUnpaidInvoicesAPI(clientId);

            // Ensure we're properly handling the response
            if (Array.isArray(invoicesData)) {
                setUnpaidInvoices(invoicesData);

                if (invoicesData.length === 0) {
                    toastify('No unpaid invoices found for this customer', 'info');
                }
            } else {
                setUnpaidInvoices([]);
                toastify('Unable to fetch unpaid invoices', 'error');
            }
        } catch (error) {
            console.error(`Error fetching unpaid invoices: ${error.message}`);
            setUnpaidInvoices([]);
            toastify(error.message || 'Error fetching unpaid invoices', 'error');
        } finally {
            setLoadingInvoices(false);
        }
    };

    const handleInvoiceSelect = (e, invoice) => {
        // Calculate amount to pay for this invoice
        const totalAmount = invoice.totalAmount || 0;
        const amountPaid = invoice.amountPaid || 0;
        const amountToPay = totalAmount - amountPaid;

        let updatedSelection;
        if (e.checked) {
            // Check if adding this invoice would exceed the remaining overpayment
            if (amountToPay > remainingOverpayment) {
                toastify('This invoice exceeds the remaining overpayment amount', 'warn');
                return;
            }

            // Add to selection if within budget
            updatedSelection = [...selectedInvoices, invoice];

            // Reduce remaining overpayment
            const newRemainingAmount = remainingOverpayment - amountToPay;
            setRemainingOverpayment(newRemainingAmount);
        } else {
            // If unchecking, restore the amount to remaining overpayment
            const newRemainingAmount = remainingOverpayment + amountToPay;
            setRemainingOverpayment(newRemainingAmount);

            // Remove from selection
            updatedSelection = selectedInvoices.filter(item => item._id !== invoice._id);
        }

        setSelectedInvoices(updatedSelection);
    };

    const handleApplyOverpayment = async () => {
        try {
            setLoading(true);

            if (selectedInvoices.length === 0) {
                throw new Error('No invoices selected to apply overpayment');
            }

            const facilityId = await getItem('selectedFacilityId');

            // Extract the source invoice ID
            let sourceInvoiceId = null;

            // Check all possible locations for the invoice ID
            if (selectedPayment && selectedPayment.invoice) {
                // Check for direct _id
                if (selectedPayment.invoice._id) {
                    sourceInvoiceId = selectedPayment.invoice._id;
                }
                // Check for invoiceId property
                else if (selectedPayment.invoice.invoiceId) {
                    sourceInvoiceId = selectedPayment.invoice.invoiceId;
                }
                // Check for nested data
                else if (selectedPayment.invoice.data && selectedPayment.invoice.data._id) {
                    sourceInvoiceId = selectedPayment.invoice.data._id;
                }
            }

            // If still not found, try to find in the root of selectedPayment
            if (!sourceInvoiceId && selectedPayment._id) {
                sourceInvoiceId = selectedPayment._id;
            }

            if (!sourceInvoiceId) {
                throw new Error('Could not determine source invoice ID');
            }

            // Prepare request payload
            const payload = {
                sourceInvoiceId: sourceInvoiceId,
                selectedInvoices: selectedInvoices.map(inv => inv._id),
                overpaymentAmount: overpaymentAmount
            };

            // Make the request
            const response = await makeRequest2(
                `${applyOverpayment}/${facilityId}`,
                'POST',
                payload
            );

            if (response.success) {
                await refreshPayments();
                toastify('Overpayment applied successfully', 'success');
            } else {
                throw new Error(response.message || 'Failed to apply overpayment');
            }
        } catch (error) {
            console.error(`Apply overpayment error: ${error.message}`);
            toastify(error.message, 'error');
        } finally {
            setOverpaymentDialogVisible(false);
            setSelectedPayment(null);
            setSelectedInvoices([]);
            setUnpaidInvoices([]);
            setOverpaymentAmount(0);
            setRemainingOverpayment(0);
            setLoading(false);
        }
    };

    // Handler for preparing the overpayment dialog
    const handlePrepareOverpayment = async (rowData, fetchInvoiceDetailsAPI, fetchUnpaidInvoicesAPI) => {
        try {
            setLoading(true);
            setSelectedPayment(rowData);

            // Get the full invoice details since the cash payment only has minimal info
            const invoiceId = rowData.invoice?.invoiceId;
            const accountNumber = rowData.invoice?.accountNumber;

            // Fetch the full invoice to get balanceBroughtForward and other details
            const fullInvoice = await fetchInvoiceDetailsAPI(invoiceId, accountNumber);

            if (fullInvoice) {
                // Use appropriate overpayment amount
                let overpayAmount = 0;

                if (fullInvoice.balanceBroughtForward < 0) {
                    // If the invoice has a negative balance brought forward, use that as the overpayment
                    overpayAmount = Math.abs(fullInvoice.balanceBroughtForward);
                }
                else if (rowData.reconciliationDetails?.overpayAmount) {
                    // If payment has reconciliation details with overpayment, use that
                    overpayAmount = rowData.reconciliationDetails.overpayAmount;
                }
                else if (fullInvoice.overpay) {
                    // If invoice has overpay field, use that
                    overpayAmount = fullInvoice.overpay;
                }

                if (overpayAmount <= 0) {
                    throw new Error('No overpayment amount available to apply');
                }

                setOverpaymentAmount(overpayAmount);
                setRemainingOverpayment(overpayAmount);

                // Create a merged payment object with the full invoice data
                const enhancedPayment = {
                    ...rowData,
                    invoice: fullInvoice
                };

                setSelectedPayment(enhancedPayment);

                // Fetch unpaid invoices for this customer
                await fetchUnpaidInvoices(rowData.client.clientId);

                // Show the dialog
                setOverpaymentDialogVisible(true);
            } else {
                throw new Error('Could not fetch full invoice details');
            }
        } catch (error) {
            console.error('Error preparing overpayment dialog:', error);
            toastify(error.message || 'Error preparing overpayment', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handler for preparing the transfer credit dialog
    const handlePrepareTransferCredit = async (rowData, fetchInvoiceDetailsAPI) => {
        try {
            setLoading(true);
            
            // Get the full invoice details
            const invoiceId = rowData.invoice?.invoiceId;
            const accountNumber = rowData.invoice?.accountNumber;
            
            // Fetch the invoice details to get balance/credit information
            const fullInvoice = await fetchInvoiceDetailsAPI(invoiceId, accountNumber);
            
            if (fullInvoice) {
                // Create a merged payment object with the invoice data
                const enhancedPayment = {
                    ...rowData,
                    invoice: fullInvoice
                };
                
                setSelectedPayment(enhancedPayment);
                
                // Check if there's credit to transfer
                const hasCredit = fullInvoice.balanceBroughtForward < 0 || 
                                 rowData.reconciliationDetails?.overpayAmount > 0;
                
                if (!hasCredit) {
                    throw new Error('No credit available to transfer from this invoice');
                }
                
                // Show the transfer credit dialog
                setTransferCreditDialogVisible(true);
            } else {
                throw new Error('Could not fetch invoice details');
            }
        } catch (error) {
            console.error('Error preparing transfer credit:', error);
            toastify(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return {
        states: {
            selectedPayment,
            approveDialogVisible,
            rejectDialogVisible,
            voidDialogVisible,
            overpaymentDialogVisible,
            transferCreditDialogVisible,
            comments,
            loading,
            unpaidInvoices,
            selectedInvoices,
            overpaymentAmount,
            remainingOverpayment,
            loadingInvoices
        },
        setters: {
            setSelectedPayment,
            setApproveDialogVisible,
            setRejectDialogVisible,
            setVoidDialogVisible,
            setOverpaymentDialogVisible,
            setTransferCreditDialogVisible,
            setComments,
            setUnpaidInvoices,
            setSelectedInvoices,
            setOverpaymentAmount,
            setRemainingOverpayment,
            setLoadingInvoices,
            setLoading
        },
        handlers: {
            handleApprovePayment,
            handleRejectPayment,
            handleVoidPayment,
            handleApplyOverpayment,
            handleInvoiceSelect,
            handlePrepareOverpayment,
            handlePrepareTransferCredit,
            handleTransferCredit
        },
        utils: {
            formatCurrency,
            formatDate,
            formatPaymentMethod
        }
    };
};